import { useEffect, useState } from 'react';
import { DASHBOARD, RoutableTabsLayout } from 'src/router';
import PageTitle from 'src/components/PageTitle';
import { useGetMachineGroupQuery, useSyncSummaryQuery } from 'src/backend';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from 'src/components/Loader';
import { generatePath } from 'src/utils';
import { routes } from './routes';
import { SyncStatus, useMachineGroupContext } from './MachineGroupContext';
import MachineGroupDetailsOverview from './MachineGroupDetailsOverview';

type Params = {
  id: string | undefined;
};

function MachineGroupDetails() {
  const { id } = useParams<Params>();
  const { data, isLoading, refetch, error } = useGetMachineGroupQuery(id ?? '');
  const syncSummary = useSyncSummaryQuery(id);
  const [refetchRequired, setRefetchRequired] = useState(false);

  const navigate = useNavigate();
  const { machineType, setMachineType, setSyncStatus, setCustomerId } =
    useMachineGroupContext();

  useEffect(() => {
    if (data?.data.machineType && data.data.machineType !== machineType) {
      setMachineType(data.data.machineType);
    }
  }, [data?.data.machineType, machineType, setMachineType]);

  useEffect(() => {
    if (data?.data.customerId) {
      setCustomerId(data.data.customerId);
    }
  }, [data?.data.customerId, setCustomerId]);

  useEffect(() => {
    if (syncSummary?.data) {
      const status =
        syncSummary.data.data.groupTaskSummary?.aggregatedStatus ?? 'Unknown';
      setSyncStatus(SyncStatus[status as keyof typeof SyncStatus]);
    }
  }, [setSyncStatus, syncSummary]);

  useEffect(() => {
    if (error) {
      navigate(generatePath(DASHBOARD));
    }
  }, [error, navigate]);

  useEffect(() => {
    if (refetchRequired) {
      setRefetchRequired(false);
      refetch();
    }
  }, [refetchRequired, refetch]);

  return (
    <>
      <PageTitle subtitle={data?.data.name} />
      {isLoading ? (
        <Loader />
      ) : (
        <RoutableTabsLayout
          routes={routes}
          overview={<MachineGroupDetailsOverview />}
          refreshData={() => setRefetchRequired(true)}
        />
      )}
    </>
  );
}

export default MachineGroupDetails;
